import { useMemo } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';

import GlobalStyleV2 from 'generic/GlobalStyleV2';
import { ContentPropertyFooter } from 'generic/ContentProperty/Footer';

import formatDate from 'utils/format-article-date';
import { buildCssVar } from 'utils/style-override';
import { buildImgProps } from 'utils/media';
import {
  type ContentPropertyProps,
  type ContentPropFooterLinks,
  buildContentPropUrl,
} from 'utils/article-helpers';

import { Components } from './components';

import styles from './styles.module.scss';

import { type ArticlePage as ArticlePageType } from 'queries/graphql';
import NavigationMenu from 'generic/ContentProperty/NavigationMenu';
import NavigationLink from 'generic/ContentProperty/NavigationLink';
import { FontOverrideProvider } from 'utils/font-override-context';

import { DynamicContentHead } from 'components/DynamicHead';
import { DynamicContentBody } from 'components/DynamicBody';
import { DynamicContentBottom } from 'components/DynamicBottom';
import PreviewModeBanner from 'components/PreviewModeBanner';
import Breadcrumb from './components/Breadcrumb';
import { PreviewDataProvider } from 'utils/preview-data-context';

import { usePageView, getPageTypeFromCateogry, getMerchantTypeFromCategory } from 'utils/gtm';
import * as rudder from 'utils/rudderstack';

export const buildPage = () => {
  function ArticlePage({ preview, pageData, contentPropertyProps, links }: ArticlePageParams) {
    const { title, contentProperty, metaDescription, tag } = pageData;
    const { favicon } = contentProperty;
    const { textColor: tagTextColor, backgroundColor: tagBackgroundColor } = tag || {};

    const { theme } = contentProperty;

    const homePath = buildContentPropUrl({ propertyId: contentProperty.id });

    const categoryUrl = buildContentPropUrl({
      propertyId: contentProperty.id,
      categorySlug: tag.collection.category.slug,
    });
    const collectionUrl = buildContentPropUrl({
      propertyId: contentProperty.id,
      categorySlug: tag.collection.category.slug,
      collectionSlug: tag.collection.slug,
    });
    const tagUrl = buildContentPropUrl({
      propertyId: contentProperty.id,
      categorySlug: tag.collection.category.slug,
      collectionSlug: tag.collection.slug,
      tagSlug: tag.slug,
    });

    const breadcrumbItems = useMemo(() => {
      return [
        {
          name: 'Home',
          url: homePath,
        },
        {
          name: tag.collection.category.name,
          url: categoryUrl,
        },
        {
          name: tag.collection.name,
          url: collectionUrl,
        },
        {
          name: title,
        },
      ];
    }, [
      homePath,
      tag.collection.category.name,
      tag.collection.name,
      categoryUrl,
      collectionUrl,
      title,
    ]);

    const { authorName, mainHeading, coverImage, publishDate } = pageData;

    const { asPath } = useRouter();

    const pageType = getPageTypeFromCateogry(tag?.collection?.category?.name);
    const merchantName = getMerchantTypeFromCategory(
      tag?.collection?.category?.slug,
      contentProperty.name
    );

    usePageView({ pageType, path: asPath, merchantName });
    rudder.usePageView({ pageType, path: asPath, merchantName });

    return (
      <PreviewDataProvider preview={preview}>
        <FontOverrideProvider {...contentPropertyProps}>
          {preview && <PreviewModeBanner />}

          <DynamicContentBody contentProperty={contentProperty} />
          <DynamicContentHead
            metaDescription={metaDescription}
            canonicalUrl={contentPropertyProps.canonicalUrl}
          />

          <Head>
            <title>{title}</title>
            <meta property="og:title" content={title}></meta>
            <meta name="twitter:title" content={title}></meta>
            <link rel="icon" href={favicon?.src} />
            <Breadcrumb items={breadcrumbItems} />
          </Head>

          <GlobalStyleV2 {...theme} />
          <style jsx>{`
            .root {
              ${buildCssVar(
                '--tag-background-color',
                tagBackgroundColor,
                'var(--background-color-grey)'
              )}
              ${buildCssVar('--tag-text-color', tagTextColor, 'var(--text-color-dark)')}
            }
          `}</style>
          <NavigationMenu contentProperty={contentProperty} menuLinks={links} />
          <div className={`root ${styles.container}`}>
            {/* @lkuich - Disabled for the time being */}
            {/* <div className={styles.header}>
              <div className={styles.logo_wrapper}>
                <Link className={styles.logo_btn} href={homePath}>
                  {logo && <Image {...buildImgProps(logo)} alt={logo.alt} />}
                </Link>
              </div>
            </div> */}
            <div className={styles.wrapper}>
              <div className={styles.breadcrumb}>
                <Link href={homePath}>Home</Link>
                <span>/</span>
                <NavigationLink
                  propertyId={contentProperty.id}
                  categorySlug={tag.collection.category.slug}
                  displayText={tag.collection.category.name}
                />
                <span>/</span>
                <NavigationLink
                  propertyId={contentProperty.id}
                  categorySlug={tag.collection.category.slug}
                  collectionSlug={tag.collection.slug}
                  displayText={tag.collection.name}
                />
              </div>
              {tag && (
                <div className={styles.tag_wrapper}>
                  <Link href={tagUrl} passHref>
                    <p className={styles.tag}>{tag.name}</p>
                  </Link>
                  {publishDate && (
                    <>
                      <p>&#x2022;</p>
                      <p>{formatDate(publishDate)}</p>
                    </>
                  )}
                </div>
              )}
              <h1 className={styles.main_heading}>{mainHeading}</h1>
              <p className={styles.author}>
                {authorName && <span>Words By: </span>}
                <span>{authorName}</span>
              </p>
              {coverImage && (
                <div className={styles.img_wrapper}>
                  <Image
                    {...buildImgProps(coverImage)}
                    alt={coverImage.alt}
                    sizes="800px"
                    priority
                  />
                </div>
              )}
            </div>

            <Components components={pageData.components} />
          </div>
          {contentProperty.footer && (
            <ContentPropertyFooter
              contentProperty={contentProperty}
              apiBaseUrl={contentPropertyProps.apiBaseUrl}
              footerLinks={links}
            />
          )}
          <DynamicContentBottom klaviyoPublicKey={contentProperty.klaviyoPublicKey} />
        </FontOverrideProvider>
      </PreviewDataProvider>
    );
  }

  return ArticlePage;
};

type ArticlePageParams = {
  preview: boolean;
  pageData: ArticlePageType;
  contentPropertyProps: ContentPropertyProps;
  links: ContentPropFooterLinks;
};
